<template>
  <div
    class="page-container"
  >
    <Toast />
    <MainTitle 
      title="Resources"
    />

    <div class="app-content-container">
      <div class="content-wrapper">
        <div
          v-if="!subjectIsSample"
          class="search-section"
        >
          <v-text-field
            v-model="searchQuery"
            variant="outlined"
            density="compact"
            :placeholder="isMobile ? 'Search...' : 'Search on the web...'"
            hide-details
            class="search-field"
            @keyup.enter="search"
          >
            <template #append-inner>
              <font-awesome-icon
                icon="magnifying-glass"
                style="cursor:pointer"
                @click="search"
              />
            </template>
          </v-text-field>
        </div>
        <div
          v-if="subjectIsSample"
          class="level-filter-section"
        >
          <v-select
            v-model="selectedLevel"
            :items="levelOptions"
            variant="outlined"
            density="compact"
            label="Level"
            hide-details
            class="level-filter-field"
            data-test="level-filter"
          />
        </div>
        <div class="resource-menu">
          <v-btn
            v-if="!subjectIsSample"
            class="ns-btn-primary"
            :elevation="0"
            aria-haspopup="true"
            aria-controls="overlay_menu"
            @click="toggle"
          >
            <font-awesome-icon
              v-if="isMobile"
              icon="plus"
              style="margin-right:6px"
            />
            {{ isMobile ? 'Add Resource' : 'Upload Resource' }}
          </v-btn>
          <Menu 
            id="overlay_menu" 
            ref="menu" 
            :model="items" 
            :popup="true" 
          />
          <ResourceUploadDialog 
            v-model:visible="isVisible" 
            :subject-id="subjectId"
            :is-file-upload="isFileUpload"
            @update-visibility="dialogVisiblility"
            @show-toast="onShowToast"
          />
          <ResourceWebSearchDialog
            ref="webSearchDialog"
            v-model:visible="isWebSearchVisible"
            :subject-id="subjectId"
            @update-visibility="webDialogVisibility"
            @add-url="addWebResource"
          />
          <v-btn
            v-if="!subjectIsSample"
            class="ns-btn-primary"
            :elevation="0"
            style="margin-left:10px"
            @click="openGenerateDialog"
          >
            <font-awesome-icon
              icon="wand-magic-sparkles"
              style="margin-right:6px"
            />
            Generate Resources
          </v-btn>
          <GenerateResourcesDialog
            v-model:visible="isGenerateVisible"
            :subject-id="subjectId"
            @show-toast="onShowToast"
          />
        </div>
        <!-- Loading state -->
        <div 
          v-show="isLoadingResources && filteredResourceList.length === 0"
          class="loading-state"
          data-test="resources-loader"
        >
          <ProgressSpinner 
            style="width: 50px; height: 50px;" 
            stroke-width="8" 
            fill="transparent"
            animation-duration=".5s"
          />
          <p style="margin-top: 10px;">Loading resources...</p>
        </div>

        <!-- Resources list -->
        <div 
          v-show="!isLoadingResources || filteredResourceList.length > 0"
          class="resources-grid" 
          data-test="resources-grid"
        >
          <div 
            v-for="(resource, index) in filteredResourceList" 
            :key="resource.id"
            class="resource-item"
          >
            <ResourceCard 
              :resource="resource"
              :data-test="`resource-item-${index}`"
              @deleted="removeResource"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Menu from 'primevue/menu';
import Toast from 'primevue/toast';
import ProgressSpinner from 'primevue/progressspinner';

import MainTitle from "@/components/title/MainTitle.vue";
import ResourceUploadDialog from "@/components/knowledgebase/ResourceUploadDialog.vue";
import ResourceWebSearchDialog from "@/components/knowledgebase/ResourceWebSearchDialog.vue";
import ResourceCard from "@/components/knowledgebase/ResourceCard.vue";
import GenerateResourcesDialog from "@/components/knowledgebase/GenerateResourcesDialog.vue";

import { getWithAuthentication, createAuthenticatedAxiosInstance } from "@/services/api";
import { BASE_URL, LIBRARY_RESOURCE_ENDPOINT } from "@/assets/constants/api_endpoints";
import { 
          RESOURCE_TYPE_URL, 
          RESOURCE_UPLOAD_STATUS_COMPLETED, 
          RESOURCE_UPLOAD_STATUS_START_SUCCESS, 
          RESOURCE_UPLOAD_STATUS_START_FAILED, 
          RESOURCE_UPLOAD_STATUS_OCR_PARSING, 
          RESOURCE_UPLOAD_STATUS_CONTENT_PARSING, 
          RESOURCE_STATUS_GENERATION_IN_PROGRESS, 
          RESOURCE_STATUS_GENERATION_PARSING, 
          RESOURCE_STATUS_GENERATION_COMPLETED, 
          RESOURCE_STATUS_GENERATION_FAILED, 
          DIFFICULTY_BEGINNER, 
          DIFFICULTY_INTERMEDIATE, 
          DIFFICULTY_ADVANCED 
} from "@/assets/constants/constants";
import { useSocketCommunication } from "@/components/knowledgebase/composables/useSocketCommunication";
import { StorageService } from "@/services/StorageService";


export default {
  components: {
    MainTitle,
    Menu,
    ResourceUploadDialog,
    ResourceWebSearchDialog,
    ResourceCard,
    GenerateResourcesDialog,
    Toast,
    ProgressSpinner
  },
  data() {
    return {
      subjectId: null,
      subjectIsSample: false,
      isVisible: false,
      isWebSearchVisible: false,
      isGenerateVisible: false,
      resourceList: [],
      isLoadingResources: true,
      isFileUpload: true,
      searchQuery: '',
      selectedLevel: DIFFICULTY_INTERMEDIATE,
      levelOptions: [DIFFICULTY_BEGINNER, DIFFICULTY_INTERMEDIATE, DIFFICULTY_ADVANCED],
      socketCommunication: null,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
      items: [
          {
              label: 'Upload from File',
              icon: 'pi pi-upload',
              command: () => {
                this.openFileUploadDialog()
              }
          },
          {
              label: 'Upload from URL',
              icon: 'pi pi-link',
              command: () => {
                this.openURLUploadDialog()
              }
          }
        ],
        ui_update_statuses: [
                              RESOURCE_UPLOAD_STATUS_COMPLETED, 
                              RESOURCE_UPLOAD_STATUS_START_SUCCESS, 
                              RESOURCE_UPLOAD_STATUS_START_FAILED, 
                              RESOURCE_UPLOAD_STATUS_CONTENT_PARSING, 
                              RESOURCE_UPLOAD_STATUS_OCR_PARSING,
                              RESOURCE_STATUS_GENERATION_IN_PROGRESS, 
                              RESOURCE_STATUS_GENERATION_PARSING,
                              RESOURCE_STATUS_GENERATION_COMPLETED, 
                              RESOURCE_STATUS_GENERATION_FAILED
                            ]
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth <= 599;
    },
    filteredResourceList() {
      if (!this.subjectIsSample) {
        return this.resourceList;
      }
      return this.resourceList.filter(resource => resource.level === this.selectedLevel);
    }
  },
  mounted() {
      // Restore from route params or localStorage
      this.subjectId = this.$route.params.id;
      let isSampleParam = this.$route.params.is_sample;
      
      // If no route params, try to restore from localStorage
      if (!this.subjectId) {
        const subjectContext = StorageService.getSubjectContext();
        if (subjectContext) {
          this.subjectId = subjectContext.subjectId;
          isSampleParam = subjectContext.isSample;
        }
      }
      
      this.subjectIsSample = isSampleParam === 'true' || isSampleParam === true;
      
      // Save subject context for page refresh scenarios
      if (this.subjectId) {
        StorageService.saveSubjectContext(this.subjectId, this.subjectIsSample);
      }
      
      this.getResourceList();
      this.initializeWebSocketListener();
      window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    if (this.socketCommunication) {
      this.socketCommunication.closeSocket();
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    removeResource(resourceId) {
      this.resourceList = this.resourceList.filter(r => r.id !== resourceId);
    },
    toggle(event) {
      this.$refs.menu.toggle(event)
    },
    openURLUploadDialog(){
      this.isVisible = true
      this.isFileUpload = false
    },
    openFileUploadDialog(){
      this.isVisible = true
      this.isFileUpload = true
    },
    openWebSearchDialog(){
      this.isWebSearchVisible = true
    },
    openGenerateDialog() {
      this.isGenerateVisible = true
    },
    webDialogVisibility(value){
      this.isWebSearchVisible = value
    },
    dialogVisiblility(value){
      this.isVisible = value
    },
    search() {
      if (this.searchQuery?.trim()) {
        this.isWebSearchVisible = true;
        // Pass the search query to the dialog
        this.$nextTick(() => {
          const dialog = this.$refs.webSearchDialog;
          if (dialog) {
            dialog.setQueryAndSearch(this.searchQuery);
          }
        });
      }
    },
    async addWebResource(url) {
      try {
        const uploadUrl = BASE_URL + LIBRARY_RESOURCE_ENDPOINT;
        const formData = new FormData();
        formData.append('subject_id', this.subjectId);
        formData.append('resource_type', RESOURCE_TYPE_URL);
        formData.append('resource_url', url);
        formData.append('file_details', url);

        const axiosInstance = createAuthenticatedAxiosInstance();

        await axiosInstance.post(uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        this.getResourceList();
      } catch (error) {
        this.$router.push({ name: "LoginForm", params: { loggedOut: true } });
      } finally {
        this.isWebSearchVisible = false
      }
    },
    async getResourceList() {
      try {
        this.isLoadingResources = true;
        const response = await getWithAuthentication(BASE_URL, LIBRARY_RESOURCE_ENDPOINT + `?subject_id=${this.subjectId}&is_sample=${this.subjectIsSample}`);
        if (response && response.status === 200) {
          this.resourceList = response.data
            .filter(item => item.is_deleted == false && item.upload_status !== 'upload_start_failed')
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        }
      } catch (error) {
        this.$router.push({ name: "LoginForm", params: { loggedOut: true } });
      } finally {
        this.isLoadingResources = false;
      }
    },
    initializeWebSocketListener() {
      if (this.socketCommunication) return; // Already initialized
      
      this.socketCommunication = useSocketCommunication();
      const authToken = localStorage.getItem('authToken');
      this.socketCommunication.initializeSocket(authToken);
      
      this.socketCommunication.setupSocketHandlers(
        (messageInfo) => {
          
          // Handle resource status updates
          if (messageInfo.type === 'resource_status_update') {
            
            // Update resource list if status is in ui_update_statuses and subject matches (or subject_id is null)
            const subjectMatches = messageInfo.subject_id === null ||
                String(messageInfo.subject_id) === String(this.subjectId);
            if (this.ui_update_statuses.includes(messageInfo.upload_status) && subjectMatches) {
                  if (messageInfo.upload_status === RESOURCE_UPLOAD_STATUS_START_FAILED) {
                    this.$toast.add({ severity: 'error', summary: 'Upload Failed', detail: `Resource failed to upload.`, life: 5000 });
                  }
                  if (messageInfo.upload_status === RESOURCE_STATUS_GENERATION_COMPLETED) {
                    this.$toast.add({ severity: 'success', summary: 'Generation Complete', detail: 'Your resources have been generated and are ready to read.', life: 5000 });
                  }
                  if (messageInfo.upload_status === RESOURCE_STATUS_GENERATION_FAILED) {
                    this.$toast.add({ severity: 'error', summary: 'Generation Failed', detail: 'One or more resources failed to generate. Please try again.', life: 5000 });
                  }
                  this.getResourceList();
            }
          } 
        },
        (error) => {
          console.error("WebSocket error:", error);
        },
        () => {
          console.log("WebSocket closed");
        }
      );
    },
    onShowToast(toastData) {
      this.$toast.add(toastData);
    }
  },
};
</script>

<style scoped>
.content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-5);
  padding-left: var(--space-8);
}

.search-section {
  margin: var(--space-4) 0;
  display: flex;
  justify-content: center;
  width: 100%;
}

.search-field {
  width: 50%;
}

.level-filter-section {
  margin: var(--space-4) 0;
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.level-filter-field {
  width: 200px;
}

.resource-menu {
  width: 100%;
  text-align: right;
  margin-bottom: var(--space-5);
}

.resources-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: var(--space-3);
  width: 100%;
  margin-top: var(--space-5);
}

.resource-item {
  width: 90px;
  display: inline-block;
  margin: 0;
  height: 90px;
}

@media (max-width: 599px) {
  .content-wrapper {
    padding: var(--space-3);
  }

  .search-section {
    margin: var(--space-2) 0;
  }

  .search-field {
    width: 100%;
  }

  .level-filter-section {
    margin: var(--space-2) 0;
    justify-content: center;
  }

  .level-filter-field {
    width: 100%;
  }

  .resource-menu {
    width: 100%;
    text-align: right;
    margin-bottom: var(--space-3);
  }

  .resources-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: var(--space-2);
    margin-top: var(--space-3);
  }

  .resource-item {
    width: 100%;
    display: flex;
    justify-content: center;
    height: auto;
  }

}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 200px;
  color: var(--color-text-secondary);
  gap: 10px;
}
</style>